/**
 * Error Handling Utilities
 * Provides helper functions to extract and format error information
 */

import { AxiosError } from 'axios';

/**
 * Extract detailed error information from AxiosError or generic error
 * @param error - The error object (AxiosError, Error, or unknown)
 * @returns Formatted error details string
 */
export function getErrorDetails(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const statusText = error.response?.statusText;
    const responseData = error.response?.data;
    const url = error.config?.url || error.request?.responseURL || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    
    let details = `[${method} ${url}] Status: ${status || 'N/A'} ${statusText || ''}`;
    
    if (responseData) {
      try {
        const dataStr = typeof responseData === 'string' 
          ? responseData 
          : JSON.stringify(responseData, null, 2);
        details += `\nResponse: ${dataStr}`;
      } catch {
        details += `\nResponse: ${String(responseData)}`;
      }
    }
    
    if (error.message) {
      details += `\nMessage: ${error.message}`;
    }
    
    return details;
  }
  
  if (error instanceof Error) {
    return error.message || String(error);
  }
  
  return String(error);
}

/**
 * Check if error is an AxiosError with a specific status code
 * @param error - The error object
 * @param statusCode - The HTTP status code to check for
 * @returns true if error is AxiosError with the specified status code
 */
export function isAxiosErrorWithStatus(error: unknown, statusCode: number): boolean {
  return error instanceof AxiosError && error.response?.status === statusCode;
}

